/**
 * Jyotish Tarang — Feedback Modal v2.2
 * Floating button on Dashboard, opens modal with star rating + feature checkboxes
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

function getToken() {
  return sessionStorage.getItem('access_token') || localStorage.getItem('access_token') || '';
}
async function apiFetch(url: string, options?: any) {
  const token = getToken();
  const h: any = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers: { ...h, ...(options?.headers || {}) } });
}

export default function FeedbackModal() {
  const { t } = useTranslation();
  const [open,     setOpen]     = useState(false);
  const [category, setCategory] = useState('feedback');
  const [rating,   setRating]   = useState(0);
  const [hovered,  setHovered]  = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [message,  setMessage]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [done,     setDone]     = useState(false);
  const [error,    setError]    = useState('');

  const CATEGORIES: { value: string; label: string; icon: string }[] = [
    { value: 'feedback',           label: t('feedbackModal.categories.feedback'),   icon: '💬' },
    { value: 'bug',                label: t('feedbackModal.categories.bug'),        icon: '🐞' },
    { value: 'feature_request',    label: t('feedbackModal.categories.feature'),    icon: '💡' },
    { value: 'support_complaint',  label: t('feedbackModal.categories.support'),    icon: '⚠️' },
  ];

  const FEATURES: string[] = t('feedbackModal.features', { returnObjects: true }) as unknown as string[];

  function toggleFeature(f: string) {
    setSelected(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  }

  async function handleSubmit() {
    if (!rating && !message.trim()) {
      setError(t('feedbackModal.errors.needRatingOrMessage'));
      return;
    }
    setLoading(true); setError('');
    try {
      const r: any = await apiFetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({ category, rating, features: selected, message }),
      });
      const d: any = await r.json();
      if (!r.ok) { setError(d.error || t('feedbackModal.errors.submitFailed')); return; }
      setDone(true);
    } catch { setError(t('common.networkError')); }
    finally { setLoading(false); }
  }

  function handleClose() {
    setOpen(false);
    setTimeout(() => { setDone(false); setCategory('feedback'); setRating(0); setSelected([]); setMessage(''); setError(''); }, 300);
  }

  return (
    <>
      {/* Floating button */}
      <button onClick={() => setOpen(true)}
              style={{
                position:'fixed', bottom:24, right:24, zIndex:1000,
                background:'var(--indigo)', color:'#fff',
                border:'none', borderRadius:24, padding:'10px 18px',
                fontSize:13, fontWeight:600, cursor:'pointer',
                boxShadow:'0 4px 16px rgba(0,0,0,0.2)',
                display:'flex', alignItems:'center', gap:6,
              }}>
        💬 {t('feedbackModal.floatingButton')}
      </button>

      {/* Backdrop */}
      {open && (
        <div onClick={handleClose}
             style={{
               position:'fixed', inset:0, zIndex:1001,
               background:'rgba(0,0,0,0.4)', backdropFilter:'blur(2px)',
             }}/>
      )}

      {/* Modal */}
      {open && (
        <div style={{
          position:'fixed', top:'50%', left:'50%', zIndex:1002,
          transform:'translate(-50%,-50%)',
          background:'#fff', borderRadius:14,
          width:'min(520px, 95vw)', maxHeight:'85vh',
          overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)',
        }}>
          {/* Header */}
          <div style={{ background:'var(--indigo)', padding:'16px 20px', borderRadius:'14px 14px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:16, fontWeight:700, color:'#F4C430' }}>💬 {t('feedbackModal.title')}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', marginTop:2 }}>{t('feedbackModal.subtitle')}</div>
            </div>
            <button onClick={handleClose}
                    style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:20, cursor:'pointer', lineHeight:1 }}>
              ✕
            </button>
          </div>

          <div style={{ padding:'20px' }}>
            {done ? (
              <div style={{ textAlign:'center', padding:'32px 20px' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🙏</div>
                <h3 style={{ color:'var(--indigo)', marginBottom:8 }}>{t('feedbackModal.thankYou')}</h3>
                <p style={{ color:'var(--ink2)', fontSize:13 }}>
                  {t('feedbackModal.thankYouBody')}
                </p>
                <button onClick={handleClose} className="btn btn-primary" style={{ marginTop:16 }}>
                  {t('common.close')}
                </button>
              </div>
            ) : (
              <>
                {/* Category */}
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--indigo)', marginBottom:8 }}>
                    {t('feedbackModal.aboutWhat')}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                    {CATEGORIES.map(c => (
                      <label key={c.value} style={{
                        display:'flex', gap:8, alignItems:'center', cursor:'pointer',
                        padding:'8px 10px', borderRadius:6,
                        background: category === c.value ? 'var(--teall)' : 'var(--ivory)',
                        border:`1px solid ${category === c.value ? 'var(--teal)' : 'var(--parch2)'}`,
                        fontSize:12, fontWeight: category === c.value ? 600 : 400,
                        color: category === c.value ? 'var(--teal)' : 'var(--ink)',
                        transition:'all 0.15s',
                      }}>
                        <input type="radio" name="feedback-category"
                               checked={category === c.value}
                               onChange={() => setCategory(c.value)}
                               style={{ accentColor:'var(--teal)', flexShrink:0 }}/>
                        {c.icon} {c.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Star rating */}
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--indigo)', marginBottom:8 }}>
                    {t('feedbackModal.overallRating')}
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n}
                              onClick={() => setRating(n)}
                              onMouseEnter={() => setHovered(n)}
                              onMouseLeave={() => setHovered(0)}
                              style={{
                                background:'none', border:'none', cursor:'pointer',
                                fontSize:32, lineHeight:1, padding:'2px',
                                color: n <= (hovered || rating) ? '#F4C430' : 'var(--parch2)',
                                transition:'color 0.1s',
                              }}>
                        ★
                      </button>
                    ))}
                    {rating > 0 && (
                      <span style={{ fontSize:12, color:'var(--ink3)', alignSelf:'center', marginLeft:6 }}>
                        {(t('feedbackModal.ratingWords', { returnObjects: true }) as unknown as string[])[rating]}
                      </span>
                    )}
                  </div>
                </div>

                {/* Feature checkboxes */}
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--indigo)', marginBottom:10 }}>
                    {t('feedbackModal.whichFeatures')}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                    {FEATURES.map(f => (
                      <label key={f} style={{
                        display:'flex', gap:8, alignItems:'center', cursor:'pointer',
                        padding:'6px 8px', borderRadius:6,
                        background: selected.includes(f) ? 'var(--teall)' : 'var(--ivory)',
                        border:`1px solid ${selected.includes(f) ? 'var(--teal)' : 'var(--parch2)'}`,
                        fontSize:12, color: selected.includes(f) ? 'var(--teal)' : 'var(--ink)',
                        transition:'all 0.15s',
                      }}>
                        <input type="checkbox"
                               checked={selected.includes(f)}
                               onChange={() => toggleFeature(f)}
                               style={{ accentColor:'var(--teal)', flexShrink:0 }}/>
                        {f}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--indigo)', marginBottom:6 }}>
                    {t('feedbackModal.yourMessage')}
                  </div>
                  <textarea value={message} onChange={e => setMessage(e.target.value)}
                            placeholder={t('feedbackModal.messagePlaceholder') as string}
                            style={{
                              width:'100%', minHeight:90, padding:'10px 12px',
                              border:'1px solid var(--parch2)', borderRadius:8,
                              fontSize:13, fontFamily:'inherit', color:'var(--ink)',
                              resize:'vertical', boxSizing:'border-box',
                            }}/>
                </div>

                {error && <div className="info-box info-red" style={{ marginBottom:12, fontSize:12 }}>{error}</div>}

                <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                  <button onClick={handleClose} className="btn btn-ghost">{t('common.cancel')}</button>
                  <button onClick={handleSubmit} disabled={loading} className="btn btn-primary">
                    {loading ? t('feedbackModal.submitting') : `🙏 ${t('feedbackModal.submit')}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
